import { action, atom } from "@reatom/core"
import isEqual from "lodash.isequal"

import { CatalogSearchQuery } from "modules/PsyCatalog/common/searchQuery"
import { FetchSearchProfilesResponse } from "utils/interfaces"
import { searchPromotedRequest, searchRequest } from "modules/PsyCatalog/common/api"
import { FormState } from "modules/PsyCatalog/common/searchQuery/schema"
import { Analytics } from "utils/analytics"
import { competencesAtom, formAtom } from "modules/PsyCatalog/common/models/filterForm"
import { goToPage, paginationAtom } from "modules/PsyCatalog/common/models/pagination"
import { profilesCachedAtom } from "modules/PsyCatalog/common/models/profilesCache"
import { ReportedProblemsStorage } from "utils/ReportedProblemsStorage"

const lastSuccessfullyLoadedStateAtom = atom<CatalogSearchQuery | undefined>(undefined)

export const loadingAtom = atom<"loading" | "success" | "error" | "empty" | "idle">("idle")

function sendAnalytics(query: FormState) {
    Analytics.search(query)
    query.session_time_intervals.forEach((sessionTimeInterval) => {
        Analytics.search_by_session_time_intervals({ sessionTimeInterval })
    })
}
export const searchRequestResultAtom = atom<FetchSearchProfilesResponse>({
    profiles: [],
    matching_count: 0,
    pages_count: 0,
    pages: [],
    query: {
        source: {
            mode: "search",
        },
    },
})

const promotedProfilesCache = new Map<string, FetchSearchProfilesResponse>()
export const promotedProfilesAtom = atom((ctx) => {
    const loadingStatus = ctx.spy(loadingAtom)
    if (loadingStatus === "success") {
        const competences = ctx.get(competencesAtom.inputValue).sort().join()
        return promotedProfilesCache.get(competences)?.profiles || []
    }
    return []
})
export const runSearchRequest = action((ctx, state: FormState, pageNumber?: number) => {
    if (ctx.get(searchDisabledAtom)) return Promise.resolve()

    loadingAtom(ctx, "loading")

    return ctx.schedule(() => {
        ctx.get(async () => {
            const competencesA = ctx.get(competencesAtom.inputValue)
            const competences = competencesA.sort().join()

            if (competencesA.length > 0) {
                ReportedProblemsStorage.store({ reported_problems_id: competencesA })
            }

            const promoted = promotedProfilesCache.get(competences)
            sendAnalytics(state)
            const [data, promotedResponse] = await Promise.all([
                searchRequest(state),
                promoted ? Promise.resolve(promoted) : searchPromotedRequest(state.competences),
            ])

            searchRequestResultAtom(ctx, () => data)
            promotedProfilesCache.set(competences, promotedResponse)
            lastSuccessfullyLoadedStateAtom(ctx, () => state)

            if (data.matching_count > 0) {
                profilesCachedAtom(ctx, () => {
                    return {
                        [0]: data?.profiles,
                    }
                })
                loadingAtom(ctx, "success")
            } else {
                Analytics.empty_search_result(state)
                loadingAtom(ctx, "empty")
            }
            paginationAtom(ctx, (state) => ({
                ...state,
                pagesCount: data.pages_count,
            }))
            goToPage(ctx, pageNumber || 1)
        }).catch((error) => {
            console.error(error)
            loadingAtom(ctx, "error")
        })
    })
})

export const searchDisabledAtom = atom((ctx) => {
    const isLoading = ctx.spy(loadingAtom) === "loading"
    const currentState = ctx.spy(formAtom)
    return isLoading || isEqual(currentState, ctx.get(lastSuccessfullyLoadedStateAtom))
})
